import { getTheatricalRelease } from 'src/services/Theatrical'
import { TheatricalPayItForwardView } from 'src/views/PayItForward/TheatricalPayItForwardView'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { PayItForwardOfferDataProvider } from '@/contexts/PayItForwardOffersDataContext/PayItForwardOffersDataContext'
import { runExperimentsOnServer } from '@/experimentation/server'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { getIndexForOfferThatImpactsClosestTo100People, getOffersForProject, Offer } from '@/services/PifService'
import { TheatricalReleaseResult } from '@/services/Theatrical/queries'
import { TheatricalReleaseRegion } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import DatadogRumFull from '@/utils/datadog/DatadogRumFull'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getRegionFromParams } from '@/utils/nextUtils/paramsUtils'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'

interface PayItForwardProjectCheckoutPageProps {
  cloudinaryImageTransformation: string
  initialOfferIndex: number
  locale: string
  offers: Offer[]
  theatricalRelease: TheatricalReleaseResult
  theatricalSlug: string
  newPifLayout?: boolean
  pifNameInCreditsEnabled?: boolean
}

export const PayItForwardProjectCheckoutPage: NextPageWithLayout<PayItForwardProjectCheckoutPageProps> = ({
  cloudinaryImageTransformation,
  locale,
  offers,
  theatricalRelease,
  theatricalSlug,
  newPifLayout,
  pifNameInCreditsEnabled,
}) => {
  const { t } = useTranslate('pif')
  const theatricalName = theatricalRelease?.title

  return (
    <DefaultLayout
      regions={theatricalRelease?.theatricalReleaseRegions as TheatricalReleaseRegion[]}
      isDarkMode={false}
    >
      <DatadogRumFull />
      <div className="bg-core-gray-100">
        <Seo
          {...getBasicPageMetaData({
            title: t('pifTheatricalTitleAngel', 'Pay it Forward for {{theatricalName}} on Angel', {
              theatricalName,
            }),
            description: t(
              'pifTheatricalDescription',
              'By Paying it Forward to {{theatricalName}}, you help audiences see this deeply moving and monumental movie for free. Help us get this movie to as many people as possible.',
              { theatricalName },
            ),
            cloudinaryImagePath: `/${theatricalRelease?.region?.seoImage}.webp`,
            cloudinaryTransformation: cloudinaryImageTransformation,
            locale,
            path: `${paths.payItForward.index}/${theatricalSlug}`,
          })}
        />
        <PayItForwardOfferDataProvider offers={offers}>
          <MarketingVideoPopup />
          <TheatricalPayItForwardView
            theatricalRelease={theatricalRelease}
            newPifLayout={newPifLayout}
            pifNameInCreditsEnabled={pifNameInCreditsEnabled}
          />
        </PayItForwardOfferDataProvider>
      </div>
    </DefaultLayout>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler<PayItForwardProjectCheckoutPageProps>(
  async ({ federationClient, locale, next404, params, req, res }) => {
    const region = getRegionFromParams(params)
    const theatricalSlug = getItemOrFirstEntry(params?.slug)
    if (!theatricalSlug) return next404()

    const [offers, theatricalRelease, translations] = await Promise.all([
      getOffersForProject({ projectSlug: theatricalSlug }, federationClient),
      getTheatricalRelease({ theatricalSlug, client: federationClient, region }),
      loadTranslations(locale, ['common', 'pif', 'account']),
    ])

    if (!theatricalRelease || !offers?.length) return next404()

    const experiments =
      theatricalSlug === slugs.david
        ? await runExperimentsOnServer(req, res, [
            { experimentKey: 'david-pif-old-vs-new-v2', defaultValue: false },
            { experimentKey: 'pif-name-in-credits', defaultValue: false },
          ])
        : null

    const newPifLayout = Boolean(experiments?.['david-pif-old-vs-new-v2'])
    const pifNameInCreditsEnabled = Boolean(experiments?.['pif-name-in-credits'])

    const initialOfferIndex = getIndexForOfferThatImpactsClosestTo100People(offers)
    const cloudinaryImageTransformation = 'c_scale'

    return {
      props: {
        cloudinaryImageTransformation,
        initialOfferIndex,
        locale,
        offers,
        theatricalSlug,
        theatricalRelease,
        newPifLayout,
        pifNameInCreditsEnabled,
        ...translations,
      },
    }
  },
)

PayItForwardProjectCheckoutPage.getLayout = (page) => {
  return page
}

export default PayItForwardProjectCheckoutPage
